import { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus, FileText, Clock, IndianRupee, CalendarDays, Paperclip,
  CheckCircle2, XCircle, Loader2, Send, MessageSquare, ChevronDown,
  Building2, Star, User, Upload, X, File, Eye, Filter
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";

const SERVICE_TYPES = [
  "GST Filing", "Income Tax Filing", "Audit Support",
  "Company Registration", "Accounting Support", "Tax Consultation",
  "Business Consulting", "Transfer Pricing", "NRI Taxation"
];

interface ServiceRequest {
  id: string;
  ca_profile_id: string;
  requester_user_id: string;
  company_name: string;
  service_type: string;
  description: string | null;
  budget: string | null;
  deadline: string | null;
  status: string;
  created_at: string;
}

interface Proposal {
  id: string;
  service_request_id: string;
  ca_profile_id: string;
  ca_user_id: string;
  proposed_amount: number;
  proposed_timeline: string | null;
  cover_letter: string;
  status: string;
  created_at: string;
  ca_profiles?: { name: string; firm_name: string | null; city: string; avg_rating: number; avatar_url: string | null } | null;
}

interface Attachment {
  id: string;
  service_request_id: string;
  file_name: string;
  file_url: string;
  file_size: number;
  file_type: string | null;
  created_at: string;
}

function formatSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const statusConfig: Record<string, { label: string; cls: string }> = {
  open: { label: "Open", cls: "bg-blue-500/10 text-blue-600 border-blue-500/20" },
  pending: { label: "Pending", cls: "bg-yellow-500/10 text-yellow-600 border-yellow-500/20" },
  in_progress: { label: "In Progress", cls: "bg-accent/10 text-accent border-accent/20" },
  accepted: { label: "Accepted", cls: "bg-green-500/10 text-green-600 border-green-500/20" },
  completed: { label: "Completed", cls: "bg-green-500/10 text-green-600 border-green-500/20" },
  declined: { label: "Declined", cls: "bg-destructive/10 text-destructive border-destructive/20" },
  cancelled: { label: "Cancelled", cls: "bg-muted text-muted-foreground border-border" },
};

// ── Create Request Dialog ──
function CreateRequestDialog({ open, onOpenChange, onCreated }: { open: boolean; onOpenChange: (v: boolean) => void; onCreated: () => void }) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [companyName, setCompanyName] = useState("");
  const [serviceType, setServiceType] = useState("");
  const [description, setDescription] = useState("");
  const [budget, setBudget] = useState("");
  const [deadline, setDeadline] = useState("");
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleSubmit = async () => {
    if (!user || !companyName.trim() || !serviceType) {
      toast({ title: "Please fill required fields", variant: "destructive" });
      return;
    }
    setLoading(true);

    // Create the request (using a dummy ca_profile_id for open requests)
    const { data: req, error } = await supabase.from("service_requests").insert({
      requester_user_id: user.id,
      ca_profile_id: "00000000-0000-0000-0000-000000000000",
      company_name: companyName.trim(),
      service_type: serviceType,
      description: description.trim() || null,
      budget: budget || null,
      deadline: deadline || null,
      status: "open",
    } as any).select("id").single();

    if (error || !req) {
      toast({ title: "Failed to create request", description: error?.message, variant: "destructive" });
      setLoading(false);
      return;
    }

    // Upload attachments
    for (const file of pendingFiles) {
      const path = `${user.id}/${req.id}/${Date.now()}-${file.name}`;
      const { data: upload } = await supabase.storage.from("service-attachments").upload(path, file);
      if (upload) {
        const { data: { publicUrl } } = supabase.storage.from("service-attachments").getPublicUrl(upload.path);
        await supabase.from("service_request_attachments").insert({
          service_request_id: req.id,
          file_name: file.name,
          file_url: publicUrl,
          file_size: file.size,
          file_type: file.type,
          uploaded_by: user.id,
        } as any);
      }
    }

    toast({ title: "Service request created!", description: "CAs can now submit proposals." });
    setCompanyName(""); setServiceType(""); setDescription(""); setBudget(""); setDeadline(""); setPendingFiles([]);
    onOpenChange(false);
    onCreated();
    setLoading(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileText className="h-5 w-5 text-accent" /> New Service Request</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div>
            <label className="text-xs font-medium text-foreground mb-1.5 block">Company Name *</label>
            <Input value={companyName} onChange={(e) => setCompanyName(e.target.value)} placeholder="Your company name" />
          </div>
          <div>
            <label className="text-xs font-medium text-foreground mb-1.5 block">Service Type *</label>
            <Select value={serviceType} onValueChange={setServiceType}>
              <SelectTrigger><SelectValue placeholder="Select service type" /></SelectTrigger>
              <SelectContent>
                {SERVICE_TYPES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-medium text-foreground mb-1.5 block">Description *</label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Describe your requirements in detail..." rows={4} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-foreground mb-1.5 block">Budget Range</label>
              <Select value={budget} onValueChange={setBudget}>
                <SelectTrigger><SelectValue placeholder="Select budget" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Under ₹10,000">Under ₹10,000</SelectItem>
                  <SelectItem value="₹10,000 - ₹25,000">₹10,000 - ₹25,000</SelectItem>
                  <SelectItem value="₹25,000 - ₹50,000">₹25,000 - ₹50,000</SelectItem>
                  <SelectItem value="₹50,000 - ₹1,00,000">₹50,000 - ₹1,00,000</SelectItem>
                  <SelectItem value="₹1,00,000+">₹1,00,000+</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-foreground mb-1.5 block">Deadline</label>
              <Input type="date" value={deadline} onChange={(e) => setDeadline(e.target.value)} min={new Date().toISOString().split("T")[0]} />
            </div>
          </div>

          {/* Attachments */}
          <div>
            <label className="text-xs font-medium text-foreground mb-1.5 block">Attachments</label>
            <div
              onClick={() => inputRef.current?.click()}
              className="border-2 border-dashed rounded-lg p-4 text-center cursor-pointer hover:border-primary/50 hover:bg-muted/30 transition-all"
            >
              <Upload className="h-5 w-5 text-muted-foreground mx-auto mb-1" />
              <p className="text-xs text-muted-foreground">Click to attach files (PDF, DOC, images)</p>
              <input ref={inputRef} type="file" multiple accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.xlsx" className="hidden"
                onChange={(e) => { if (e.target.files) setPendingFiles((p) => [...p, ...Array.from(e.target.files!)]); e.target.value = ""; }} />
            </div>
            {pendingFiles.length > 0 && (
              <div className="space-y-1.5 mt-2">
                {pendingFiles.map((f, i) => (
                  <div key={i} className="flex items-center gap-2 px-2 py-1.5 bg-muted/40 rounded-md border border-border text-xs">
                    <File className="h-3.5 w-3.5 text-primary flex-shrink-0" />
                    <span className="flex-1 truncate text-foreground">{f.name}</span>
                    <span className="text-muted-foreground">{formatSize(f.size)}</span>
                    <button onClick={() => setPendingFiles((p) => p.filter((_, j) => j !== i))} className="text-muted-foreground hover:text-destructive">
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <Button onClick={handleSubmit} disabled={loading} className="w-full gap-1.5">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Submit Request
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Proposal Card ──
function ProposalCard({ proposal, isOwner, onAccept, onReject }: {
  proposal: Proposal; isOwner: boolean;
  onAccept: (id: string) => void; onReject: (id: string) => void;
}) {
  const ca = proposal.ca_profiles;
  const sts = statusConfig[proposal.status] || statusConfig.pending;

  return (
    <Card className="bg-card border-border">
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0 overflow-hidden">
            {ca?.avatar_url ? (
              <img src={ca.avatar_url} alt={ca.name} className="w-full h-full object-cover" />
            ) : (
              <User className="h-4 w-4 text-primary" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h4 className="text-sm font-semibold text-foreground">{ca?.name || "CA"}</h4>
              {ca?.firm_name && <span className="text-[11px] text-muted-foreground">• {ca.firm_name}</span>}
              <Badge variant="outline" className={`text-[10px] ${sts.cls}`}>{sts.label}</Badge>
            </div>
            <div className="flex items-center gap-3 mt-1">
              {ca?.city && <span className="text-[10px] text-muted-foreground">{ca.city}</span>}
              {ca?.avg_rating ? (
                <span className="text-[10px] text-muted-foreground flex items-center gap-0.5">
                  <Star className="h-2.5 w-2.5 fill-yellow-400 text-yellow-400" /> {Number(ca.avg_rating).toFixed(1)}
                </span>
              ) : null}
            </div>
            <div className="mt-2 flex items-center gap-4">
              <span className="text-sm font-semibold text-foreground flex items-center gap-0.5">
                <IndianRupee className="h-3.5 w-3.5" />{Number(proposal.proposed_amount).toLocaleString("en-IN")}
              </span>
              {proposal.proposed_timeline && (
                <span className="text-xs text-muted-foreground flex items-center gap-1">
                  <Clock className="h-3 w-3" /> {proposal.proposed_timeline}
                </span>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-2 leading-relaxed">{proposal.cover_letter}</p>
          </div>
        </div>

        {isOwner && proposal.status === "submitted" && (
          <div className="flex gap-2 mt-3 pt-3 border-t border-border/30">
            <Button size="sm" className="flex-1 gap-1 text-xs" onClick={() => onAccept(proposal.id)}>
              <CheckCircle2 className="h-3.5 w-3.5" /> Accept Proposal
            </Button>
            <Button size="sm" variant="outline" className="flex-1 gap-1 text-xs text-destructive" onClick={() => onReject(proposal.id)}>
              <XCircle className="h-3.5 w-3.5" /> Reject
            </Button>
          </div>
        )}
        {proposal.status === "accepted" && (
          <div className="mt-2 pt-2 border-t border-border/30">
            <Badge className="bg-green-500/10 text-green-600 border-green-500/20 text-[10px]">
              <CheckCircle2 className="h-3 w-3 mr-1" /> Accepted
            </Badge>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── Submit Proposal Dialog (for CAs) ──
function SubmitProposalDialog({ requestId, open, onOpenChange, onSubmitted }: {
  requestId: string; open: boolean; onOpenChange: (v: boolean) => void; onSubmitted: () => void;
}) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [amount, setAmount] = useState("");
  const [timeline, setTimeline] = useState("");
  const [coverLetter, setCoverLetter] = useState("");

  const handleSubmit = async () => {
    if (!user || !amount || !coverLetter.trim()) {
      toast({ title: "Please fill required fields", variant: "destructive" });
      return;
    }

    setLoading(true);
    // Get CA profile
    const { data: profile } = await supabase.from("ca_profiles").select("id").eq("user_id", user.id).single();
    if (!profile) {
      toast({ title: "CA profile not found", description: "Please create your CA profile first.", variant: "destructive" });
      setLoading(false);
      return;
    }

    const { error } = await supabase.from("service_proposals").insert({
      service_request_id: requestId,
      ca_profile_id: profile.id,
      ca_user_id: user.id,
      proposed_amount: parseFloat(amount),
      proposed_timeline: timeline.trim() || null,
      cover_letter: coverLetter.trim(),
    } as any);

    if (error) {
      toast({ title: "Failed to submit proposal", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Proposal submitted!" });
      setAmount(""); setTimeline(""); setCoverLetter("");
      onOpenChange(false);
      onSubmitted();
    }
    setLoading(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Send className="h-5 w-5 text-accent" /> Submit Proposal</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div>
            <label className="text-xs font-medium text-foreground mb-1.5 block">Proposed Amount (₹) *</label>
            <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="e.g. 25000" />
          </div>
          <div>
            <label className="text-xs font-medium text-foreground mb-1.5 block">Estimated Timeline</label>
            <Input value={timeline} onChange={(e) => setTimeline(e.target.value)} placeholder="e.g. 2 weeks, 30 days" />
          </div>
          <div>
            <label className="text-xs font-medium text-foreground mb-1.5 block">Cover Letter *</label>
            <Textarea value={coverLetter} onChange={(e) => setCoverLetter(e.target.value)}
              placeholder="Explain your approach, experience, and why you're the right fit..." rows={5} />
          </div>
          <Button onClick={handleSubmit} disabled={loading} className="w-full gap-1.5">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Submit Proposal
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Request Detail View ──
function RateCADialog({ request, open, onOpenChange, onRated }: {
  request: ServiceRequest; open: boolean; onOpenChange: (v: boolean) => void; onRated: () => void;
}) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [rating, setRating] = useState(5);
  const [reviewText, setReviewText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!user) return;
    setSubmitting(true);
    const { error } = await supabase.from("ca_reviews").insert({
      ca_profile_id: request.ca_profile_id,
      reviewer_user_id: user.id,
      rating,
      review_text: reviewText.trim() || null,
      service_request_id: request.id,
    } as any);
    if (error) {
      toast({ title: "Failed to submit review", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Review submitted!", description: "Thank you for your feedback." });
      onOpenChange(false);
      setRating(5); setReviewText("");
      onRated();
    }
    setSubmitting(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Star className="h-5 w-5 text-yellow-500" /> Rate & Review CA</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div>
            <p className="text-xs font-medium text-foreground mb-2">Your Rating</p>
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4, 5].map((s) => (
                <Star
                  key={s}
                  className={`h-7 w-7 cursor-pointer transition-colors ${s <= rating ? "fill-yellow-400 text-yellow-400" : "text-muted-foreground/30 hover:text-yellow-300"}`}
                  onClick={() => setRating(s)}
                />
              ))}
              <span className="text-sm font-semibold text-foreground ml-2">{rating}/5</span>
            </div>
          </div>
          <div>
            <p className="text-xs font-medium text-foreground mb-1.5">Written Review</p>
            <Textarea
              value={reviewText}
              onChange={(e) => setReviewText(e.target.value)}
              placeholder="Share your experience working with this CA..."
              rows={4}
            />
          </div>
          <Button onClick={handleSubmit} disabled={submitting} className="w-full gap-1.5">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Star className="h-4 w-4" />}
            Submit Review
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function RequestChat({ requestId }: { requestId: string }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState<{ id: string; sender_id: string; content: string; created_at: string; is_read: boolean }[]>([]);
  const [newMessage, setNewMessage] = useState("");
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);

  const fetchMessages = useCallback(async () => {
    const { data } = await supabase
      .from("marketplace_messages")
      .select("*")
      .eq("service_request_id", requestId)
      .order("created_at", { ascending: true });
    if (data) setMessages(data);
  }, [requestId]);

  useEffect(() => {
    fetchMessages();
    const channel = supabase
      .channel(`chat-${requestId}`)
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "marketplace_messages", filter: `service_request_id=eq.${requestId}` }, () => {
        fetchMessages();
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [requestId, fetchMessages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendMessage = async () => {
    if (!user || !newMessage.trim()) return;
    setSending(true);
    const { error } = await supabase.from("marketplace_messages").insert({
      service_request_id: requestId,
      sender_id: user.id,
      content: newMessage.trim(),
    } as any);
    if (!error) {
      setNewMessage("");
      fetchMessages();
    }
    setSending(false);
  };

  return (
    <Card className="bg-card border-border">
      <CardHeader className="p-4 pb-2">
        <CardTitle className="text-xs font-semibold flex items-center gap-2">
          <MessageSquare className="h-4 w-4 text-accent" /> Messages
        </CardTitle>
      </CardHeader>
      <CardContent className="p-4 pt-0">
        <div ref={chatContainerRef} className="h-[240px] overflow-y-auto space-y-2 mb-3 pr-1">
          {messages.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <p className="text-xs text-muted-foreground">No messages yet. Start the conversation!</p>
            </div>
          ) : (
            messages.map((msg) => {
              const isMine = msg.sender_id === user?.id;
              return (
                <div key={msg.id} className={`flex ${isMine ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[75%] rounded-xl px-3 py-2 text-xs ${isMine ? "bg-primary text-primary-foreground" : "bg-muted text-foreground"}`}>
                    <p className="leading-relaxed">{msg.content}</p>
                    <p className={`text-[9px] mt-1 ${isMine ? "text-primary-foreground/60" : "text-muted-foreground"}`}>
                      {new Date(msg.created_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
                    </p>
                  </div>
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>
        <div className="flex gap-2">
          <Input
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
            placeholder="Type a message..."
            className="text-xs"
          />
          <Button size="sm" onClick={sendMessage} disabled={sending || !newMessage.trim()} className="gap-1 px-3">
            {sending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function RequestDetail({ request, onBack, isCA }: { request: ServiceRequest; onBack: () => void; isCA: boolean }) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [proposals, setProposals] = useState<Proposal[]>([]);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [loadingProposals, setLoadingProposals] = useState(true);
  const [showProposalDialog, setShowProposalDialog] = useState(false);
  const [showRateDialog, setShowRateDialog] = useState(false);
  const [hasReviewed, setHasReviewed] = useState(false);
  const isOwner = user?.id === request.requester_user_id;

  useEffect(() => {
    fetchProposals();
    fetchAttachments();
    checkReviewed();
  }, [request.id]);

  const checkReviewed = async () => {
    if (!user) return;
    const { count } = await supabase
      .from("ca_reviews")
      .select("*", { count: "exact", head: true })
      .eq("service_request_id", request.id)
      .eq("reviewer_user_id", user.id);
    setHasReviewed((count || 0) > 0);
  };

  const markComplete = async () => {
    const { error } = await supabase.from("service_requests").update({ status: "completed" }).eq("id", request.id);
    if (error) {
      toast({ title: "Failed to complete", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Service marked as completed!" });
      request.status = "completed";
      setShowRateDialog(true);
    }
  };

  const fetchProposals = async () => {
    setLoadingProposals(true);
    const { data } = await supabase
      .from("service_proposals")
      .select("*, ca_profiles(name, firm_name, city, avg_rating, avatar_url)")
      .eq("service_request_id", request.id)
      .order("created_at", { ascending: false }) as any;
    if (data) setProposals(data);
    setLoadingProposals(false);
  };

  const fetchAttachments = async () => {
    const { data } = await supabase
      .from("service_request_attachments")
      .select("*")
      .eq("service_request_id", request.id)
      .order("created_at", { ascending: true }) as any;
    if (data) setAttachments(data);
  };

  const updateProposalStatus = async (proposalId: string, status: string) => {
    const proposal = proposals.find((p) => p.id === proposalId);
    const { error } = await supabase.from("service_proposals").update({ status, updated_at: new Date().toISOString() } as any).eq("id", proposalId);
    if (error) {
      toast({ title: "Failed to update", description: error.message, variant: "destructive" });
    } else {
      toast({ title: `Proposal ${status}` });
      // If accepting, update request status and create marketplace transaction
      if (status === "accepted" && proposal) {
        await supabase.from("service_requests").update({ status: "in_progress" }).eq("id", request.id);

        // Create commission transaction (10% platform fee)
        const COMMISSION_RATE = 0.10;
        const serviceAmount = Number(proposal.proposed_amount);
        const commissionAmount = Math.round(serviceAmount * COMMISSION_RATE);
        const netAmount = serviceAmount - commissionAmount;

        await supabase.from("marketplace_transactions").insert({
          service_request_id: request.id,
          proposal_id: proposal.id,
          ca_profile_id: proposal.ca_profile_id,
          ca_user_id: proposal.ca_user_id,
          business_user_id: request.requester_user_id,
          service_amount: serviceAmount,
          commission_rate: COMMISSION_RATE,
          commission_amount: commissionAmount,
          net_amount: netAmount,
          status: "pending",
          payment_status: "unpaid",
        } as any);
      }
      fetchProposals();
    }
  };

  const sts = statusConfig[request.status] || statusConfig.open;

  return (
    <div className="space-y-4">
      <Button variant="ghost" size="sm" onClick={onBack} className="gap-1.5 text-muted-foreground">
        ← Back to Requests
      </Button>

      {/* Request Header */}
      <Card className="bg-card border-border">
        <CardContent className="p-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <h1 className="text-xl font-bold text-foreground">{request.service_type}</h1>
                <Badge variant="outline" className={`text-[10px] ${sts.cls}`}>{sts.label}</Badge>
              </div>
              <p className="text-sm text-muted-foreground flex items-center gap-2">
                <Building2 className="h-3.5 w-3.5" /> {request.company_name}
                <span className="text-border">•</span>
                {new Date(request.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {isCA && request.status === "open" && (
                <Button size="sm" className="gap-1.5" onClick={() => setShowProposalDialog(true)}>
                  <Send className="h-3.5 w-3.5" /> Submit Proposal
                </Button>
              )}
              {isOwner && request.status === "in_progress" && (
                <Button size="sm" variant="default" className="gap-1.5" onClick={markComplete}>
                  <CheckCircle2 className="h-3.5 w-3.5" /> Mark Complete & Rate
                </Button>
              )}
              {isOwner && request.status === "completed" && !hasReviewed && (
                <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setShowRateDialog(true)}>
                  <Star className="h-3.5 w-3.5" /> Rate CA
                </Button>
              )}
              {hasReviewed && (
                <Badge variant="outline" className="bg-green-500/10 text-green-600 border-green-500/20 text-[10px] gap-1">
                  <CheckCircle2 className="h-3 w-3" /> Reviewed
                </Badge>
              )}
            </div>
          </div>

          {request.description && (
            <p className="text-sm text-muted-foreground leading-relaxed mb-4">{request.description}</p>
          )}

          <div className="flex flex-wrap gap-4">
            {request.budget && (
              <div className="flex items-center gap-1.5 text-sm">
                <IndianRupee className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium text-foreground">{request.budget}</span>
              </div>
            )}
            {request.deadline && (
              <div className="flex items-center gap-1.5 text-sm">
                <CalendarDays className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium text-foreground">
                  Deadline: {new Date(request.deadline).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                </span>
              </div>
            )}
          </div>

          {/* Attachments */}
          {attachments.length > 0 && (
            <div className="mt-4 pt-4 border-t border-border/30">
              <p className="text-xs font-medium text-foreground mb-2 flex items-center gap-1.5">
                <Paperclip className="h-3.5 w-3.5" /> Attachments ({attachments.length})
              </p>
              <div className="flex flex-wrap gap-2">
                {attachments.map((att) => (
                  <a key={att.id} href={att.file_url} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-2 px-3 py-1.5 bg-muted/40 rounded-md border border-border text-xs hover:bg-muted/60 transition-colors">
                    <File className="h-3.5 w-3.5 text-primary" />
                    <span className="text-foreground">{att.file_name}</span>
                    <span className="text-muted-foreground">{formatSize(att.file_size)}</span>
                    <Eye className="h-3 w-3 text-muted-foreground" />
                  </a>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Proposals */}
      <div>
        <h2 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
          <FileText className="h-4 w-4 text-accent" />
          Proposals ({proposals.length})
        </h2>
        {loadingProposals ? (
          <div className="py-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : proposals.length === 0 ? (
          <Card className="bg-card border-border">
            <CardContent className="py-10 text-center">
              <FileText className="h-8 w-8 text-muted-foreground/30 mx-auto mb-2" />
              <p className="text-xs text-muted-foreground">No proposals yet</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {proposals.map((p) => (
              <ProposalCard
                key={p.id}
                proposal={p}
                isOwner={isOwner}
                onAccept={(id) => updateProposalStatus(id, "accepted")}
                onReject={(id) => updateProposalStatus(id, "rejected")}
              />
            ))}
          </div>
        )}
      </div>

      {/* Chat */}
      {(request.status === "in_progress" || request.status === "completed" || request.status === "accepted") && (
        <RequestChat requestId={request.id} />
      )}

      <SubmitProposalDialog
        requestId={request.id}
        open={showProposalDialog}
        onOpenChange={setShowProposalDialog}
        onSubmitted={fetchProposals}
      />

      <RateCADialog
        request={request}
        open={showRateDialog}
        onOpenChange={setShowRateDialog}
        onRated={() => { checkReviewed(); }}
      />
    </div>
  );
}

// ── Main Page ──
const ServiceRequestsPage = () => {
  const { user, role } = useAuth();
  const { toast } = useToast();
  const [requests, setRequests] = useState<ServiceRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<ServiceRequest | null>(null);
  const [filterStatus, setFilterStatus] = useState("all");
  const isCA = role === "ca";

  useEffect(() => {
    if (user) fetchRequests();
  }, [user]);

  const fetchRequests = async () => {
    setLoading(true);
    let query = supabase.from("service_requests").select("*").order("created_at", { ascending: false });

    // CAs see open requests + their own; businesses see their own
    if (!isCA) {
      query = query.eq("requester_user_id", user!.id);
    }

    const { data, error } = await query;
    if (error) {
      toast({ title: "Failed to load requests", description: error.message, variant: "destructive" });
    } else {
      setRequests((data as ServiceRequest[]) || []);
    }
    setLoading(false);
  };

  const filtered = filterStatus === "all" ? requests : requests.filter((r) => r.status === filterStatus);

  if (selectedRequest) {
    return <RequestDetail request={selectedRequest} onBack={() => { setSelectedRequest(null); fetchRequests(); }} isCA={isCA} />;
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-display text-foreground flex items-center gap-2">
            <FileText className="h-6 w-6 text-accent" />
            Service Requests
          </h1>
          <p className="text-sm text-muted-foreground">
            {isCA ? "Browse open requests and submit proposals" : "Create service requests and manage proposals from CAs"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[130px] h-8 text-xs">
              <Filter className="h-3 w-3 mr-1" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
          {!isCA && (
            <Button size="sm" className="gap-1.5" onClick={() => setShowCreate(true)}>
              <Plus className="h-4 w-4" /> New Request
            </Button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: "Total", count: requests.length, icon: FileText },
          { label: "Open", count: requests.filter((r) => r.status === "open").length, icon: Clock },
          { label: "In Progress", count: requests.filter((r) => r.status === "in_progress").length, icon: Loader2 },
          { label: "Completed", count: requests.filter((r) => r.status === "completed").length, icon: CheckCircle2 },
        ].map((s) => (
          <Card key={s.label} className="bg-card border-border">
            <CardContent className="p-3 flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center">
                <s.icon className="h-4 w-4 text-accent" />
              </div>
              <div>
                <p className="text-lg font-bold text-foreground">{s.count}</p>
                <p className="text-[10px] text-muted-foreground">{s.label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Request List */}
      {loading ? (
        <div className="py-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : filtered.length === 0 ? (
        <Card className="bg-card border-border">
          <CardContent className="py-16 text-center">
            <FileText className="h-10 w-10 text-muted-foreground/30 mx-auto mb-3" />
            <h3 className="text-sm font-semibold text-foreground mb-1">No service requests</h3>
            <p className="text-xs text-muted-foreground mb-4">
              {isCA ? "No open requests available right now." : "Create your first request to find a CA."}
            </p>
            {!isCA && (
              <Button size="sm" onClick={() => setShowCreate(true)} className="gap-1.5">
                <Plus className="h-4 w-4" /> New Request
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          <AnimatePresence>
            {filtered.map((req) => {
              const sts = statusConfig[req.status] || statusConfig.open;
              return (
                <motion.div key={req.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                  <Card className="bg-card border-border hover:border-accent/30 transition-all cursor-pointer" onClick={() => setSelectedRequest(req)}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <h3 className="text-sm font-semibold text-foreground">{req.service_type}</h3>
                            <Badge variant="outline" className={`text-[10px] ${sts.cls}`}>{sts.label}</Badge>
                          </div>
                          <p className="text-xs text-muted-foreground flex items-center gap-2">
                            <Building2 className="h-3 w-3" /> {req.company_name}
                            <span className="text-border">•</span>
                            {new Date(req.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}
                          </p>
                          {req.description && (
                            <p className="text-xs text-muted-foreground mt-1.5 line-clamp-2">{req.description}</p>
                          )}
                          <div className="flex items-center gap-3 mt-2">
                            {req.budget && (
                              <span className="text-[11px] text-foreground font-medium flex items-center gap-0.5">
                                <IndianRupee className="h-3 w-3" /> {req.budget}
                              </span>
                            )}
                            {req.deadline && (
                              <span className="text-[11px] text-muted-foreground flex items-center gap-0.5">
                                <CalendarDays className="h-3 w-3" /> {new Date(req.deadline).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}
                              </span>
                            )}
                          </div>
                        </div>
                        <ChevronDown className="h-4 w-4 text-muted-foreground -rotate-90 flex-shrink-0 mt-1" />
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      )}

      <CreateRequestDialog open={showCreate} onOpenChange={setShowCreate} onCreated={fetchRequests} />
    </div>
  );
};

export default ServiceRequestsPage;
